import type { Staff } from "@/lib/authv2/store";
import { ACTION } from "@/lib/auth/actions";
import { hasPermission } from "@/lib/authv2/permissions";

// NOTE: The DEPT-mode functions below (canEditAllPayroll, canEditDeptPayroll,
// canViewAnalytics) have no clean single BE action mapping during Model A
// migration — kept on role/dept check. Revisit if BE adds dedicated actions.

function role(s: Staff | null): string {
  return (s?.role ?? "").toLowerCase();
}
function dept(s: Staff | null): string {
  return (s?.department ?? "").toLowerCase();
}

const EDIT_ALL = new Set(["admin", "director", "accounting"]);
// Overview / Analytics (company-wide aggregates: tab "📊 Tổng quan" + "📈 Phân
// tích"). Managers never receive the cross-department data these charts need,
// so the gate is moot for them. (Vai Điều phối đã khoá 23/08/2026 — quyết
// định 0050 — nên không còn là vai cần cân nhắc ở đây.)
// Accounting is here because it DOES receive every row (see salaryScope) — the
// charts are the same numbers as the table it already reads, drawn as a graph.
// Widened by CEO decision 21/08/2026, same decision as salaryScope (0035).
const ANALYTICS = new Set(["admin", "director", "accounting"]);

// Tab "📊 Tổng quan" — RỘNG HƠN ANALYTICS một bậc (CEO chốt 01/09/2026).
//
// Trước quyết định này, Trưởng cửa hàng và Quản lý phòng KHÔNG có màn tổng
// quan nào: trưởng cửa hàng chỉ đọc được bảng liệt kê từng người của tiệm
// mình, không có một dòng tổng; quản lý phòng vào trang là rơi thẳng vào
// "Lương của tôi". CEO yêu cầu hai vai này nắm được bức tranh lương trong
// PHẠM VI CỦA HỌ, nên tab mở thêm cho `manager` và `branch_manager`.
//
// 🔴 Cổng này KHÔNG tự nó bó phạm vi, và cố ý không làm việc đó: máy chủ đã
// cắt sẵn trước khi dữ liệu rời khỏi nó (payroll/service.go `scopeModeFor` →
// manager nhận đúng phòng mình, branch_manager nhận đúng chi nhánh mình), và
// `scopeToDepts`/`scopeToBranches` tính LẠI `GrandTotal` trên phần đã cắt nên
// cả tổng cộng lẫn từng dòng đều đã trong phạm vi. Màn hình cộng từ dữ liệu
// nhận được là đủ. Đừng thêm một lớp lọc thứ hai ở đây: hai bản sao của cùng
// một luật là cách chắc chắn nhất để chúng lệch nhau.
//
// Tab "📈 Phân tích" vẫn giữ nguyên ANALYTICS — CEO chỉ hỏi về màn tổng quan,
// và biểu đồ bên đó vẽ theo cả công ty nên không nới kèm.
const OVERVIEW = new Set([
  "admin",
  "director",
  "accounting",
  "manager",
  "branch_manager",
]);

export function canReadPayroll(s: Staff | null): boolean {
  if (!s) return false;
  return hasPermission(ACTION.PayrollRead);
}

export function canFinalizePayroll(s: Staff | null): boolean {
  if (!s) return false;
  return hasPermission(ACTION.PayrollFinalize);
}

/**
 * Dải "Bảng lương đã gửi đi tới đâu" (QĐ 0296, CEO chốt 3a 05/10/2026): hẹp
 * hơn PayrollRead có chủ ý — lý do HR trả lại là chữ tự do nói về cả công ty.
 */
export function canSeePayrollCenterStatus(s: Staff | null): boolean {
  if (!s) return false;
  return hasPermission(ACTION.PayrollCenterStatusRead);
}

/**
 * Bảng "Phí đào tạo đang trừ qua lương" (QĐ 0298): khoản của cả công ty, chỉ
 * xem. Người nợ thấy khoản của mình trên phiếu lương, không cần quyền này.
 */
export function canSeePayrollTrainingFees(s: Staff | null): boolean {
  if (!s) return false;
  return hasPermission(ACTION.PayrollTrainingFeeRead);
}

/**
 * Bảng soi nâng gói — tab dành cho người giám sát (quyết định 0039).
 *
 * Cổng riêng report.upsell.read chứ không dùng payroll.read: tập người xem
 * KHÔNG trùng nhau. Đọc được lương KHÔNG có nghĩa là soi được người nâng gói
 * — ai tự mình nâng gói thì không soi bảng này. Trưởng cửa hàng thì ngược
 * lại, soi được nhưng chỉ trong cơ sở mình — phần bó cơ sở do máy chủ làm,
 * không phải chỗ này.
 */
export function canReadUpsellReport(s: Staff | null): boolean {
  if (!s) return false;
  return hasPermission(ACTION.ReportUpsellRead);
}

/** Edit any staff's payroll entry (custom additions/deductions).
 *
 * NOTE: No dedicated payroll.edit action — kept on role check during Model A. */
export function canEditAllPayroll(s: Staff | null): boolean {
  return EDIT_ALL.has(role(s));
}

/** Edit only my own department (manager of that dept).
 *
 * NOTE: Dept-scoped manager gate — kept on role/dept check during Model A. */
export function canEditDeptPayroll(s: Staff | null, deptKey: string): boolean {
  if (canEditAllPayroll(s)) return true;
  return role(s) === "manager" && dept(s) === deptKey.toLowerCase();
}

/** NOTE: Broader role set than PayrollRead; no dedicated action — kept on role
 * check during Model A. */
export function canViewAnalytics(s: Staff | null): boolean {
  return ANALYTICS.has(role(s));
}

/** Tab "Tổng quan" — xem chú thích ở OVERVIEW. Phạm vi do máy chủ cắt. */
export function canViewPayrollOverview(s: Staff | null): boolean {
  return OVERVIEW.has(role(s));
}

/**
 * Ô "quỹ lương ăn bao nhiêu phần doanh thu" (CEO chốt 01/09/2026, câu 3a:
 * doanh thu ĐÃ THU ĐỦ TIỀN).
 *
 * 🔴 Ẩn với Quản lý phòng vì với vai đó tử số và mẫu số đo hai thứ khác nhau,
 * và tỷ lệ ra sẽ là một con số VÔ NGHĨA chứ không phải một con số kém chính
 * xác. Lương của quản lý phòng bó theo PHÒNG (mọi chi nhánh), còn doanh thu
 * công nhận chỉ có theo CHI NHÁNH (máy chủ cắt bằng BranchScope, không có
 * đường cắt theo phòng). Lấy quỹ lương phòng Makeup toàn quốc chia cho doanh
 * thu một cơ sở thì ra tỷ lệ vài trăm phần trăm — người đọc không có cách nào
 * biết con số đó sai.
 *
 * Trưởng cửa hàng thì HỢP LỆ: cả lương lẫn doanh thu đều bó về đúng tiệm họ.
 */
export function canSeeRevenueRatio(s: Staff | null): boolean {
  const r = role(s);
  if (r === "manager") return false;
  return OVERVIEW.has(r);
}

/** Staff always can view their own salary slip. */
export function canViewMySalary(s: Staff | null): boolean {
  return !!s;
}

export function isAdminTier(s: Staff | null): boolean {
  return ["admin", "director"].includes(role(s));
}

// ── Salary visibility scope (restored from the old PayrollPage model) ──
//
// Old behaviour (frontend-bk auth.js canSeeSalaryData / canSeeStaffSalary +
// PayrollPage filterRecordsForUser):
//   • admin / director      → every department, every staff row
//   • accounting (kế toán trưởng) → every department (CEO decision 21/08/2026)
//   • branch_manager        → every row the BE returns (BE cuts by branch)
//   • manager               → full rows only for their own department; own row elsewhere
//   • staff /…              → own salary row only
//
// The refactor collapsed this onto a single `payroll.read` gate, which leaked
// every staff's full salary to the privileged roles + accounting. These
// helpers reinstate the per-department / per-row scoping on the FE.
// (Accounting was later granted the full table on purpose — by CEO decision,
// not by leak.)
//
// Vai Điều phối (`coordinator`) từng có một bộ phòng ban đặc quyền ở đây.
// Quyết định 0050 (23/08/2026) khoá vai này — máy chủ đã rút sạch quyền —
// nên bộ phòng ban đó đã gỡ; trưởng cửa hàng (`branch_manager`) thế chỗ.

export type SalaryScope =
  | { kind: "all" }
  | { kind: "depts"; depts: string[] }
  | { kind: "self" };

/** Which salary rows a user may see in full. See the scope model note above. */
export function salaryScope(s: Staff | null): SalaryScope {
  const r = role(s);
  // "accounting" is the chief accountant — the role that writes the salary
  // rules and sends every payslip. Must match the BE `scopeResult`; if the two
  // drift, the FE either hides rows the server sent or renders empty tabs for
  // rows it never receives. Rank-and-file accounting staff are role "staff".
  if (r === "admin" || r === "director" || r === "accounting") {
    return { kind: "all" };
  }
  // Vai Điều phối đã khoá 23/08/2026 (quyết định 0050) — rơi xuống "self" ở
  // cuối hàm. KHÔNG thêm nhánh trả "all"/"depts" cho vai này nữa.
  if (r === "branch_manager") {
    // Trưởng cửa hàng đọc lương cả tiệm. BE (payroll scopeModeForUser) cắt
    // theo địa bàn — Casdoor: mọi cơ sở được gán (Thảo: 2); database: cơ sở
    // chính. FE coi mọi dòng nhận được là hợp lệ (spec 2026-08-23 §5.7).
    return { kind: "all" };
  }
  if (r === "manager") {
    const d = dept(s);
    return { kind: "depts", depts: d ? [d] : [] };
  }
  return { kind: "self" };
}

/** Only admin/director get the cross-department "Tất cả" salary table. */
export function canSeeAllSalaryTab(s: Staff | null): boolean {
  return salaryScope(s).kind === "all";
}

/** Mã phòng GỘP của trưởng khối ảnh — gương `mergedPhotoDept` (payroll/service.go). */
const MERGED_PHOTO_DEPT = "photo";

/**
 * Phạm vi theo phòng có phủ phòng của dòng lương không — gương `deptInScope`
 * bên máy chủ: trưởng khối ảnh (phòng "photo") quản mọi phòng `photo*`. So
 * khít thì máy chủ gửi dòng thợ ảnh thẻ / profile mà màn lọc mất sạch.
 */
function deptCovers(scopeDepts: string[], recDept: string): boolean {
  const d = recDept.trim().toLowerCase();
  return scopeDepts.some((s) => s === d || (s === MERGED_PHOTO_DEPT && d.startsWith(MERGED_PHOTO_DEPT)));
}

/**
 * Department tabs (with full staff rows) this user may open, given the set of
 * departments that actually have payroll data this month.
 */
export function visibleSalaryDepts(s: Staff | null, dataDepts: string[]): string[] {
  const scope = salaryScope(s);
  if (scope.kind === "all") return dataDepts;
  if (scope.kind === "self") return [];
  return dataDepts.filter((d) => deptCovers(scope.depts, d));
}

/** Per-row guard: can this user see this specific staff's full salary row? */
export function canSeeSalaryRow(
  s: Staff | null,
  rec: { staffId?: string | null; name?: string | null; department?: string | null },
): boolean {
  const scope = salaryScope(s);
  if (scope.kind === "all") return true;
  if (scope.kind === "depts" && rec.department && deptCovers(scope.depts, rec.department)) {
    return true;
  }
  // Otherwise only the user's own row.
  if (!s) return false;
  return (!!rec.staffId && rec.staffId === s.id) || (!!rec.name && rec.name === s.name);
}
